import React, { useMemo } from 'react'
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import classNames from 'classnames'
import Message, { useTranslation } from 'maeng-utils/locale'
import useGatsbyImage from '@/hooks/useGatsbyImage'
import useIntersectionObserver from '@/hooks/useIntersectionObserver'
import { INTERSECTION_OBSERVER_ROOT_MARGIN } from '@/constants'
import { addClass } from '@/utils/dom'

type ExperienceItem = {
  image: IGatsbyImageData | null
  details: {
    title: string
    term: string
    description: string
    subDetails?: {
      subTitle: string
      contents: {
        contentTitle: string
        contentDetails: string[]
      }[]
    }[]
  }
}

const Experiences = () => {
  const { t } = useTranslation()

  useIntersectionObserver(
    (entries: IntersectionObserverEntry[]) => {
      return entries
        .filter(({ isIntersecting }) => isIntersecting)
        .forEach(({ target }) => addClass(target as HTMLElement, 'observed-shown'))
    },
    { rootMargin: INTERSECTION_OBSERVER_ROOT_MARGIN },
  )

  const [yanolja, igaworks, woowa, zimssa, develup, dable, seoultech] = useGatsbyImage([
    'yanolja.png',
    'igaworks.png',
    'woowa.png',
    'zimssa.png',
    'develup.png',
    'dable.png',
    'seoultech.png',
  ])

  const experiences = useMemo<ExperienceItem[]>(
    () => [
      {
        image: yanolja,
        details: {
          title: t('portfolio.experiences.yanolja.title'),
          term: t('portfolio.experiences.yanolja.term'),
          description: t('portfolio.experiences.yanolja.description'),
          subDetails: t('portfolio.experiences.yanolja.subDetails'),
        },
      },
      {
        image: igaworks,
        details: {
          title: t('portfolio.experiences.igaworks.title'),
          term: t('portfolio.experiences.igaworks.term'),
          description: t('portfolio.experiences.igaworks.description'),
          subDetails: t('portfolio.experiences.igaworks.subDetails'),
        },
      },
      {
        image: zimssa,
        details: {
          title: t('portfolio.experiences.zimssa.title'),
          term: t('portfolio.experiences.zimssa.term'),
          description: t('portfolio.experiences.zimssa.description'),
          subDetails: t('portfolio.experiences.zimssa.subDetails'),
        },
      },
      {
        image: woowa,
        details: {
          title: t('portfolio.experiences.woowa.title'),
          term: t('portfolio.experiences.woowa.term'),
          description: t('portfolio.experiences.woowa.description'),
          subDetails: t('portfolio.experiences.woowa.subDetails'),
        },
      },
      {
        image: seoultech,
        details: {
          title: t('portfolio.experiences.seoultech.title'),
          term: t('portfolio.experiences.seoultech.term'),
          description: t('portfolio.experiences.seoultech.description'),
          subDetails: t('portfolio.experiences.seoultech.subDetails'),
        },
      },
      {
        image: develup,
        details: {
          title: t('portfolio.experiences.develup.title'),
          term: t('portfolio.experiences.develup.term'),
          description: t('portfolio.experiences.develup.description'),
          subDetails: t('portfolio.experiences.develup.subDetails'),
        },
      },
      {
        image: dable,
        details: {
          title: t('portfolio.experiences.dable.title'),
          term: t('portfolio.experiences.dable.term'),
          description: t('portfolio.experiences.dable.description'),
          subDetails: t('portfolio.experiences.dable.subDetails'),
        },
      },
    ],
    [dable, develup, igaworks, seoultech, t, woowa, yanolja, zimssa],
  )

  return (
    <section className="mb-12">
      <Message
        i18nKey="portfolio.experiences.title"
        className="text-3xl font-extrabold pt-5 pb-8"
        component="h2"
      />
      <div className="flex flex-col gap-6 px-4 mb-20">
        {experiences.map(({ details: { title, term, description, subDetails }, image }) => (
          <div
            key={title}
            className={classNames(
              'flex p-4 rounded-2xl border border-solid border-[#e3e6e9] gap-4',
              'observed observed-hidden',
            )}
          >
            <div className="flex items-center min-w-[4rem] max-w-[6rem] h-full">
              {image && (
                <GatsbyImage
                  className="object-contain aspect-square bg-white rounded-xl shadow-lg border border-solid border-[#f0f0f0]"
                  image={image}
                  alt={title}
                />
              )}
            </div>
            <div className="w-full">
              <h5
                className={classNames(
                  'text-lg flex flex-col gap-1 mb-1',
                  'md:flex-row md:items-center',
                )}
              >
                <span className="font-semibold">{title}</span>
                <span className="font-semibold text-xs text-[#8a8e94]">{term}</span>
              </h5>
              <div className="flex container">
                <input id={title} className={classNames('hidden peer')} type="checkbox" />
                <div
                  className={classNames(
                    'overflow-hidden text-ellipsis relative text-justify leading-6 max-h-[4.5em] min-h-0',
                    "before:content-[''] before:h-[calc(100%-26px)] before:float-right",
                    'group peer-checked:max-h-none',
                  )}
                >
                  <label
                    className={classNames(
                      'relative float-right clear-both ml-[20px] px-[8px] leading-6 text-[#ffaf19] bg-[#ffaf191a] rounded-lg cursor-pointer',
                      "after:content-['펼치기']",
                      "before:content-['...'] before:absolute before:left-[-5px] before:text-[#2d2020] before:translate-x-[-100%]",
                      "group-[.peer:checked+&]:after:content-['접기']",
                      'group-[.peer:checked+&]:before:invisible',
                    )}
                    htmlFor={title}
                  />
                  {description}
                  {subDetails?.map(({ subTitle, contents }) => (
                    <section key={subTitle} className="mt-6">
                      <h3 className="text-lg font-semibold semi-highlight-[#ffaf19] mb-3">
                        {subTitle}
                      </h3>
                      <h3 className="font-semibold before:content-['•'] before:mr-2">작업내용</h3>
                      <ul className="ml-6 flex flex-col gap-4">
                        {contents.map(({ contentTitle, contentDetails }) => (
                          <li key={contentTitle}>
                            <h6 className="ml-1 font-medium mb-2">{contentTitle}</h6>
                            <ul className="ml-3 flex flex-col gap-1">
                              {contentDetails.map((content) => (
                                <li className="before:content-['-'] before:mr-2" key={content}>
                                  {content}
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export default Experiences
